import React, { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FileIcon, defaultStyles } from "react-file-icon";
import JSZip from "jszip";
import ReactMarkdown from "react-markdown";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
  Maximize2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  Archive,
  File,
  Download,
} from "lucide-react";

import {
  getContentType,
  formatFileSize,
  getFileTypeFromUrl,
} from "@/helpers/documentPreview";

interface FileMetadata {
  contentType: string;
  contentLength: number;
  lastModified?: Date;
  etag?: string;
}

interface EnhancedFilePreviewProps {
  fileUrl: string;
  fileName?: string;
  metadata?: FileMetadata | null;
  showMetadata?: boolean;
  showControls?: boolean;
  className?: string;
}

// Custom styles for file icons
interface FileIconStyle {
  labelColor?: string;
  glyphColor?: string;
}

const styleDefObj: { [key: string]: FileIconStyle } = {
  pdf: { labelColor: "#d32f2f", glyphColor: "#ffffff" },
  docx: { labelColor: "#2b579a", glyphColor: "#ffffff" },
  xlsx: { labelColor: "#217346", glyphColor: "#ffffff" },
  pptx: { labelColor: "#d24726", glyphColor: "#ffffff" },
  zip: { labelColor: "#8e24aa", glyphColor: "#ffffff" },
  png: { labelColor: "#4caf50", glyphColor: "#ffffff" },
  jpg: { labelColor: "#4caf50", glyphColor: "#ffffff" },
  jpeg: { labelColor: "#4caf50", glyphColor: "#ffffff" },
  gif: { labelColor: "#4caf50", glyphColor: "#ffffff" },
  mp4: { labelColor: "#ff5722", glyphColor: "#ffffff" },
  mp3: { labelColor: "#9c27b0", glyphColor: "#ffffff" },
  txt: { labelColor: "#607d8b", glyphColor: "#ffffff" },
  md: { labelColor: "#212121", glyphColor: "#ffffff" },
};

const EnhancedFilePreview: React.FC<EnhancedFilePreviewProps> = ({
  fileUrl,
  fileName,
  metadata,
  //   showMetadata = false,
  showControls = true,
  className = "",
}) => {
  // Extract filename and extension
  const extractedFileName =
    fileName || fileUrl.split("/").pop() || "Unknown File";
  const fileExtension = extractedFileName.split(".").pop()?.toLowerCase() || "";
  const fileType = getFileTypeFromUrl(fileUrl);

  // Determine content type
  const contentType = metadata?.contentType || getContentType(fileExtension);

  // State management
  const [numPages, setNumPages] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [zipContents, setZipContents] = useState<
    Array<{ name: string; size: number }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize PDF worker
  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }, []);

  // Load markdown content
  useEffect(() => {
    if (fileExtension === "md") {
      setIsLoading(true);
      fetch(fileUrl)
        .then((response) => response.text())
        .then((text) => {
          setMarkdownContent(text);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching markdown:", error);
          setMarkdownContent("Failed to load markdown content");
          setError("Failed to load markdown content");
          setIsLoading(false);
        });
    }
  }, [fileUrl, fileExtension]);

  // Load ZIP contents
  useEffect(() => {
    if (fileExtension === "zip" || contentType === "application/zip") {
      setIsLoading(true);
      fetch(fileUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const zip = new JSZip();
          return zip.loadAsync(blob);
        })
        .then((contents) => {
          const files = Object.keys(contents.files).map((filename) => ({
            name: filename,
            size: 0, // Will be calculated
          }));

          Promise.all(
            files.map(async (file) => {
              try {
                const data =
                  await contents.files[file.name].async("uint8array");
                return {
                  name: file.name,
                  size: data.length,
                };
              } catch {
                return {
                  name: file.name,
                  size: 0,
                };
              }
            })
          ).then((resolvedFiles) => {
            setZipContents(resolvedFiles);
            setIsLoading(false);
          });
        })
        .catch((error) => {
          console.error("Error fetching zip contents:", error);
          setZipContents([]);
          setError("Failed to load ZIP contents");
          setIsLoading(false);
        });
    }
  }, [fileUrl, fileExtension, contentType]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const handleFullscreen = () => setIsFullscreen(!isFullscreen);

  const getFileTypeIcon = () => {
    switch (fileType) {
      case "image":
        return <ImageIcon className="w-4 h-4" />;
      case "video":
        return <Video className="w-4 h-4" />;
      case "audio":
        return <Music className="w-4 h-4" />;
      case "archive":
        return <Archive className="w-4 h-4" />;
      case "document":
      case "spreadsheet":
      case "presentation":
        return <FileText className="w-4 h-4" />;
      default:
        return <File className="w-4 h-4" />;
    }
  };

  const renderControls = () => {
    if (!showControls) return null;

    return (
      <div className="flex items-center gap-2 p-2 bg-gray-50 border-b">
        <Button variant="outline" size="sm" onClick={handleZoomOut}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm text-gray-600 px-2">
          {Math.round(zoom * 100)}%
        </span>
        <Button variant="outline" size="sm" onClick={handleZoomIn}>
          <ZoomIn className="w-4 h-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button variant="outline" size="sm" onClick={handleRotate}>
          <RotateCw className="w-4 h-4" />
        </Button>

        <Button variant="outline" size="sm" onClick={handleFullscreen}>
          <Maximize2 className="w-4 h-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button variant="outline" size="sm" asChild>
          <a href={fileUrl} download={extractedFileName}>
            <Download className="w-4 h-4" />
          </a>
        </Button>

        <div className="flex-1" />
        <div>
          {/* <div className="font-medium text-gray-600">Filename</div> */}
          <div className="break-all">{extractedFileName}</div>
        </div>
      </div>
    );
  };

  // Office document preview
  if (
    /\.(docx?|xlsx?|pptx?)$/i.test(fileExtension) ||
    contentType.includes("officedocument") ||
    contentType.includes("msword") ||
    contentType.includes("ms-excel") ||
    contentType.includes("ms-powerpoint")
  ) {
    const msOfficeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <div
          className="relative w-full"
          style={{ height: isFullscreen ? "100vh" : "75vh" }}
        >
          <iframe
            src={msOfficeUrl}
            className="w-full h-full border-0"
            title={extractedFileName}
            onError={(e) => {
              const iframe = e.target as HTMLIFrameElement;
              const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
              iframe.src = googleDocsUrl;
            }}
          />
        </div>
      </div>
    );
  }

  // Markdown preview
  if (fileExtension === "md") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 text-center">Loading markdown...</div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">{error}</div>
            ) : (
              <div className="p-4 prose prose-sm max-w-none">
                <ReactMarkdown>{markdownContent}</ReactMarkdown>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ZIP file preview
  if (contentType === "application/zip" || fileExtension === "zip") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" />
              ZIP Archive Contents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-4">Loading ZIP contents...</div>
            ) : error ? (
              <div className="text-center py-4 text-red-500">{error}</div>
            ) : zipContents.length > 0 ? (
              <>
                <div className="space-y-2">
                  {zipContents.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="truncate flex-1">{file.name}</span>
                      <Badge variant="secondary">
                        {formatFileSize(file.size)}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div>
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={fileUrl}
                  >
                    <Button variant={"outline"}>
                      <Download className="mr-2" /> Download
                    </Button>
                  </a>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                No files found in the ZIP archive.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Image preview
  if (fileType === "image") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardContent className="p-0">
            <div className="relative w-full min-h-96 bg-gray-100 flex items-center justify-center">
              <img
                src={fileUrl}
                alt={extractedFileName}
                className="max-w-full max-h-full object-contain"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transition: "transform 0.2s ease",
                }}
                onError={() => setError("Failed to load image")}
              />
              {error && (
                <div className="absolute inset-0 flex items-center justify-center text-red-500">
                  {error}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Video preview
  if (fileType === "video") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardContent className="p-0">
            <video
              src={fileUrl}
              controls
              className="w-full max-h-96"
              onError={() => setError("Failed to load video")}
            >
              Your browser does not support the video tag.
            </video>
            {error && (
              <div className="p-4 text-center text-red-500">{error}</div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Audio preview
  if (fileType === "audio") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col items-center gap-4">
              <Music className="w-16 h-16 text-gray-400" />
              <audio
                src={fileUrl}
                controls
                className="w-full max-w-md"
                onError={() => setError("Failed to load audio")}
              >
                Your browser does not support the audio tag.
              </audio>
              {error && <div className="text-red-500">{error}</div>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PDF preview
  if (fileExtension === "pdf") {
    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    };

    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <div
          className="bg-gray-100 p-4 overflow-auto"
          style={{ height: isFullscreen ? "100vh" : "75vh" }}
        >
          <Document
            file={fileUrl}
            error="Failed to load PDF"
            className="flex flex-col items-center"
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() => setError("Failed to load PDF")}
          >
            {Array.from(new Array(numPages || 0), (_, index) => (
              <div key={`page_${index + 1}`} className="mb-4">
                <Page
                  pageNumber={index + 1}
                  scale={zoom}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="mx-auto shadow-md"
                />
              </div>
            ))}
          </Document>
          {numPages && (
            <p className="text-center text-sm text-gray-500 mt-4">
              {numPages} page{numPages > 1 ? "s" : ""}
            </p>
          )}
          {error && (
            <div className="text-center text-red-500 mt-4">{error}</div>
          )}
        </div>
      </div>
    );
  }

  // Text file preview
  if (contentType === "text/plain" || fileExtension === "txt") {
    return (
      <div className={`w-full ${className}`}>
        {/* {renderMetadata()} */}
        {renderControls()}
        <Card>
          <CardContent className="p-0">
            <iframe
              src={fileUrl}
              className="w-full h-96 border-0"
              title={extractedFileName}
              onError={() => setError("Failed to load text file")}
            />

            {error && (
              <div className="p-4 text-center text-red-500">{error}</div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default fallback with file icon
  const customDefaultLabelColor =
    styleDefObj[fileExtension]?.labelColor ?? "#777";
  const libDefaultGlyphColor =
    defaultStyles[fileExtension as keyof typeof defaultStyles]?.labelColor;

  return (
    <div className={`w-full ${className}`}>
      {/* {renderMetadata()} */}
      <Card className="w-full max-w-sm mx-auto">
        <CardHeader>
          <CardTitle className="text-sm font-medium truncate text-center">
            {extractedFileName}
          </CardTitle>
          <div className="flex justify-center">
            <Badge variant="secondary" className="flex items-center gap-1">
              {getFileTypeIcon()}
              {fileExtension.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          <div className="w-16 h-16 mb-4">
            <FileIcon
              extension={fileExtension}
              {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
              {...styleDefObj[fileExtension]}
              labelColor={
                styleDefObj[fileExtension]?.labelColor ||
                customDefaultLabelColor
              }
              glyphColor={
                styleDefObj[fileExtension]?.glyphColor ||
                libDefaultGlyphColor ||
                customDefaultLabelColor
              }
            />
          </div>
          <div className="text-center text-sm text-gray-600 mt-4">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={fileUrl}
            >
              <Button variant={"outline"}>
                <Download className="mr-2" /> Download
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedFilePreview;
